import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import {
  addParticipant,
  addStage,
  addTransition,
  ApiError,
  createProcess,
  deleteStage,
  deleteTransition,
  fetchDepartments,
  fetchProcess,
  fetchProcesses,
  type IntensityPeriod,
  NODE_TYPE_LABELS,
  PERIOD_LABELS,
  type ProcessDetail,
  type ProcessFilters,
  type ProcessNodeType,
  type ProcessSchedule,
  type ProcessStage,
  type ProcessStatus,
  type ProcessSummary,
  removeParticipant,
  reorderStages,
  SCHEDULE_LABELS,
  searchEmployees,
  STATUS_LABELS,
  updateProcess,
  updateTransition,
  type UserProfile,
} from './api'
import ProcessGraph from './ProcessGraph'

const SCHEDULES = Object.keys(SCHEDULE_LABELS) as ProcessSchedule[]
const PERIODS = Object.keys(PERIOD_LABELS) as IntensityPeriod[]
const STATUSES = Object.keys(STATUS_LABELS) as ProcessStatus[]
const NODE_TYPES = Object.keys(NODE_TYPE_LABELS) as ProcessNodeType[]

/** «1 раз», «2 раза», «5 раз» — иначе интенсивность читается как машинный вывод. */
function timesWord(count: number): string {
  const tens = count % 100
  if (tens >= 11 && tens <= 14) return 'раз'
  const ones = count % 10
  if (ones === 1) return 'раз'
  if (ones >= 2 && ones <= 4) return 'раза'
  return 'раз'
}

function formatIntensity(count: number | null, period: IntensityPeriod | null): string {
  if (count === null || period === null) return '—'
  return `${count} ${timesWord(count)} в ${PERIOD_LABELS[period]}`
}

/** Поиск сотрудника с выпадающим списком — тот же приём, что в карточке подразделения. */
function EmployeePicker({
  label,
  value,
  onPick,
}: {
  label: string
  value: { id: number; name: string } | null
  onPick: (picked: { id: number; name: string } | null) => void
}) {
  const [query, setQuery] = useState('')

  const results = useQuery({
    queryKey: ['employee-search', query],
    queryFn: () => searchEmployees(query),
    enabled: query.trim().length >= 2,
  })

  if (value) {
    return (
      <label className="field">
        <span>{label}</span>
        <span className="picked">
          {value.name}
          <button type="button" className="link" onClick={() => onPick(null)}>
            сбросить
          </button>
        </span>
      </label>
    )
  }

  return (
    <label className="field">
      <span>{label}</span>
      <input
        value={query}
        placeholder="Начните вводить имя…"
        onChange={(event) => setQuery(event.target.value)}
      />
      {results.data && query.trim().length >= 2 && (
        <ul className="search-results inline">
          {results.data.length === 0 && <li className="muted">Ничего не найдено</li>}
          {results.data.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                className="link"
                onClick={() => {
                  onPick({ id: person.id, name: person.display_name })
                  setQuery('')
                }}
              >
                {person.display_name}
              </button>
              <span className="muted small"> · {person.department ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </label>
  )
}

function DepartmentSelect({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const departments = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments })

  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option>
        {(departments.data ?? []).map((department) => (
          <option key={department.id} value={department.id}>
            {department.name}
          </option>
        ))}
      </select>
    </label>
  )
}

/** Пара «сколько раз — за какой период». Число без периода бессмысленно, поэтому вместе. */
function IntensityFields({
  label,
  count,
  period,
  onCount,
  onPeriod,
}: {
  label: string
  count: string
  period: string
  onCount: (value: string) => void
  onPeriod: (value: string) => void
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <span className="intensity-input">
        <input
          type="number"
          min={0}
          value={count}
          placeholder="—"
          onChange={(event) => onCount(event.target.value)}
        />
        <span className="muted small">раз в</span>
        <select value={period} onChange={(event) => onPeriod(event.target.value)}>
          <option value="">—</option>
          {PERIODS.map((item) => (
            <option key={item} value={item}>
              {PERIOD_LABELS[item]}
            </option>
          ))}
        </select>
      </span>
    </label>
  )
}

function intensityPayload(count: string, period: string) {
  if (!count.trim() || !period) return { count: null, period: null }
  return { count: Number(count), period }
}

function CreateProcessForm({ onCreated }: { onCreated: (id: number) => void }) {
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [schedule, setSchedule] = useState<ProcessSchedule>('on_demand')
  const [description, setDescription] = useState('')
  const [owner, setOwner] = useState<{ id: number; name: string } | null>(null)
  const [expectedCount, setExpectedCount] = useState('')
  const [expectedPeriod, setExpectedPeriod] = useState('')
  const [actualCount, setActualCount] = useState('')
  const [actualPeriod, setActualPeriod] = useState('')

  const save = useMutation({
    mutationFn: () =>
      createProcess({
        name,
        schedule,
        description: description.trim() || null,
        owner_employee_id: owner?.id ?? null,
        expected: intensityPayload(expectedCount, expectedPeriod),
        actual: intensityPayload(actualCount, actualPeriod),
      }),
    onSuccess: (process) => {
      setName('')
      setDescription('')
      setOwner(null)
      setExpectedCount('')
      setExpectedPeriod('')
      setActualCount('')
      setActualPeriod('')
      queryClient.invalidateQueries({ queryKey: ['processes'] })
      queryClient.invalidateQueries({ queryKey: ['audit'] })
      onCreated(process.id)
    },
  })

  const error = save.error instanceof ApiError ? save.error.message : null

  return (
    <form
      className="card"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <h2>Новый процесс</h2>

      <div className="inline-fields">
        <label className="field grow">
          <span>Название</span>
          <input
            required
            value={name}
            placeholder="Согласование договора"
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Регулярность</span>
          <select
            value={schedule}
            onChange={(event) => setSchedule(event.target.value as ProcessSchedule)}
          >
            {SCHEDULES.map((item) => (
              <option key={item} value={item}>
                {SCHEDULE_LABELS[item]}
              </option>
            ))}
          </select>
        </label>

        <EmployeePicker label="Ответственный" value={owner} onPick={setOwner} />
      </div>

      <div className="inline-fields">
        <IntensityFields
          label="Ожидаемая интенсивность"
          count={expectedCount}
          period={expectedPeriod}
          onCount={setExpectedCount}
          onPeriod={setExpectedPeriod}
        />
        <IntensityFields
          label="Фактическая интенсивность"
          count={actualCount}
          period={actualPeriod}
          onCount={setActualCount}
          onPeriod={setActualPeriod}
        />
      </div>

      <label className="field">
        <span>Описание</span>
        <textarea
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <p className="muted small">
        Интенсивность можно не заполнять: неизвестная частота — это не то же самое, что ноль.
        Процесс создаётся черновиком.
      </p>

      {error && <p className="bad form-error">{error}</p>}

      <button type="submit" disabled={save.isPending || !name.trim()}>
        {save.isPending ? 'Создаём…' : 'Создать'}
      </button>
    </form>
  )
}

function ProcessRegistry({
  isAdmin,
  onOpen,
}: {
  isAdmin: boolean
  onOpen: (id: number) => void
}) {
  const [filters, setFilters] = useState<ProcessFilters>({})

  const processes = useQuery({
    queryKey: ['processes', filters],
    queryFn: () => fetchProcesses(filters),
  })

  return (
    <section>
      {isAdmin && <CreateProcessForm onCreated={onOpen} />}

      <div className="card">
        <h2>Бизнес-процессы</h2>

        <div className="inline-fields">
          <label className="field grow">
            <span>Поиск</span>
            <input
              type="search"
              value={filters.q ?? ''}
              placeholder="По названию…"
              onChange={(event) => setFilters({ ...filters, q: event.target.value })}
            />
          </label>

          <label className="field">
            <span>Статус</span>
            <select
              value={filters.status ?? ''}
              onChange={(event) =>
                setFilters({ ...filters, status: (event.target.value || undefined) as ProcessStatus })
              }
            >
              <option value="">все</option>
              {STATUSES.map((item) => (
                <option key={item} value={item}>
                  {STATUS_LABELS[item]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Регулярность</span>
            <select
              value={filters.schedule ?? ''}
              onChange={(event) =>
                setFilters({
                  ...filters,
                  schedule: (event.target.value || undefined) as ProcessSchedule,
                })
              }
            >
              <option value="">любая</option>
              {SCHEDULES.map((item) => (
                <option key={item} value={item}>
                  {SCHEDULE_LABELS[item]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {processes.isPending && <p>Загружаем…</p>}
        {processes.isError && <p className="bad">Не удалось загрузить реестр</p>}

        {processes.data?.length === 0 && <p className="muted">Ничего не найдено</p>}

        {processes.data && processes.data.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Регулярность</th>
                <th>Ожидается</th>
                <th>Фактически</th>
                <th>Ответственный</th>
                <th>Этапов</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {processes.data.map((process: ProcessSummary) => (
                <tr key={process.id}>
                  <td>
                    <button type="button" className="link" onClick={() => onOpen(process.id)}>
                      {process.name}
                    </button>
                  </td>
                  <td>{SCHEDULE_LABELS[process.schedule]}</td>
                  <td className="nowrap">
                    {formatIntensity(process.expected_count, process.expected_period)}
                  </td>
                  <td className="nowrap">
                    {formatIntensity(process.actual_count, process.actual_period)}
                  </td>
                  <td>{process.owner_name ?? '—'}</td>
                  <td>{process.node_count}</td>
                  <td>
                    <span className={`badge status-${process.status}`}>
                      {STATUS_LABELS[process.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}

function StageForm({ process }: { process: ProcessDetail }) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [type, setType] = useState<ProcessNodeType>('task')
  const [actor, setActor] = useState<{ id: number; name: string } | null>(null)
  const [departmentId, setDepartmentId] = useState('')

  const save = useMutation({
    mutationFn: () =>
      addStage(process.id, {
        title,
        type,
        actor_employee_id: actor?.id ?? null,
        department_id: departmentId ? Number(departmentId) : null,
      }),
    onSuccess: (detail) => {
      setTitle('')
      setActor(null)
      setDepartmentId('')
      queryClient.setQueryData(['process', process.id], detail)
      queryClient.invalidateQueries({ queryKey: ['processes'] })
      queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
  })

  const error = save.error instanceof ApiError ? save.error.message : null

  return (
    <form
      className="stacked-form"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="inline-fields">
        <label className="field grow">
          <span>Название этапа</span>
          <input required value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>

        <label className="field">
          <span>Тип</span>
          <select
            value={type}
            onChange={(event) => setType(event.target.value as ProcessNodeType)}
          >
            {NODE_TYPES.map((item) => (
              <option key={item} value={item}>
                {NODE_TYPE_LABELS[item]}
              </option>
            ))}
          </select>
        </label>

        {/* Исполнитель и подразделение взаимоисключающие: у этапа один лейн, а два
            назначения дали бы на вопрос «в чьём лейне этап» два разных ответа. */}
        {departmentId === '' && (
          <EmployeePicker label="Исполнитель" value={actor} onPick={setActor} />
        )}
        {actor === null && (
          <DepartmentSelect
            label="или подразделение"
            value={departmentId}
            onChange={setDepartmentId}
          />
        )}
      </div>

      {error && <p className="bad form-error">{error}</p>}

      <button type="submit" disabled={save.isPending || !title.trim()}>
        Добавить этап
      </button>
    </form>
  )
}

function TransitionForm({ process }: { process: ProcessDetail }) {
  const queryClient = useQueryClient()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [label, setLabel] = useState('')

  const save = useMutation({
    mutationFn: () =>
      addTransition(process.id, {
        from_node_key: from,
        to_node_key: to,
        label: label.trim() || null,
      }),
    onSuccess: (detail) => {
      setLabel('')
      queryClient.setQueryData(['process', process.id], detail)
      queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
  })

  const error = save.error instanceof ApiError ? save.error.message : null

  return (
    <form
      className="stacked-form"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="inline-fields">
        <label className="field">
          <span>Из этапа</span>
          <select required value={from} onChange={(event) => setFrom(event.target.value)}>
            <option value="">—</option>
            {process.nodes.map((stage) => (
              <option key={stage.key} value={stage.key}>
                {stage.title}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>В этап</span>
          <select required value={to} onChange={(event) => setTo(event.target.value)}>
            <option value="">—</option>
            {process.nodes.map((stage) => (
              <option key={stage.key} value={stage.key}>
                {stage.title}
              </option>
            ))}
          </select>
        </label>

        <label className="field grow">
          <span>Условие</span>
          <input
            value={label}
            placeholder="если согласовано"
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>
      </div>

      <p className="muted small">
        Ветвление — это несколько переходов из одного этапа. У развилки условие обязательно:
        иначе непонятно, какая ветка когда выбирается.
      </p>

      {error && <p className="bad form-error">{error}</p>}

      <button type="submit" disabled={save.isPending || !from || !to}>
        Добавить переход
      </button>
    </form>
  )
}

function ParticipantForm({ process }: { process: ProcessDetail }) {
  const queryClient = useQueryClient()
  const [person, setPerson] = useState<{ id: number; name: string } | null>(null)
  const [departmentId, setDepartmentId] = useState('')
  const [note, setNote] = useState('')

  const save = useMutation({
    mutationFn: () =>
      addParticipant(process.id, {
        employee_id: person?.id ?? null,
        department_id: departmentId ? Number(departmentId) : null,
        note: note.trim() || null,
      }),
    onSuccess: (detail) => {
      setPerson(null)
      setDepartmentId('')
      setNote('')
      queryClient.setQueryData(['process', process.id], detail)
      queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
  })

  const error = save.error instanceof ApiError ? save.error.message : null

  return (
    <form
      className="stacked-form"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="inline-fields">
        {departmentId === '' && (
          <EmployeePicker label="Исполнитель" value={person} onPick={setPerson} />
        )}
        {person === null && (
          <DepartmentSelect
            label="или подразделение"
            value={departmentId}
            onChange={setDepartmentId}
          />
        )}
        <label className="field grow">
          <span>Роль в процессе</span>
          <input
            value={note}
            placeholder="Согласует смету"
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
      </div>

      {error && <p className="bad form-error">{error}</p>}

      <button type="submit" disabled={save.isPending || (!person && !departmentId)}>
        Добавить участника
      </button>
    </form>
  )
}

function ProcessSettings({ process }: { process: ProcessDetail }) {
  const queryClient = useQueryClient()
  const [expectedCount, setExpectedCount] = useState(
    process.expected_count === null ? '' : String(process.expected_count),
  )
  const [expectedPeriod, setExpectedPeriod] = useState(process.expected_period ?? '')
  const [actualCount, setActualCount] = useState(
    process.actual_count === null ? '' : String(process.actual_count),
  )
  const [actualPeriod, setActualPeriod] = useState(process.actual_period ?? '')

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => updateProcess(process.id, patch),
    onSuccess: (detail) => {
      queryClient.setQueryData(['process', process.id], detail)
      queryClient.invalidateQueries({ queryKey: ['processes'] })
      queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
  })

  const error = save.error instanceof ApiError ? save.error.message : null

  return (
    <div className="stacked-form">
      <div className="inline-fields">
        <label className="field">
          <span>Регулярность</span>
          <select
            value={process.schedule}
            onChange={(event) => save.mutate({ schedule: event.target.value })}
          >
            {SCHEDULES.map((item) => (
              <option key={item} value={item}>
                {SCHEDULE_LABELS[item]}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Статус</span>
          <select
            value={process.status}
            onChange={(event) => save.mutate({ status: event.target.value })}
          >
            {STATUSES.map((item) => (
              <option key={item} value={item}>
                {STATUS_LABELS[item]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="inline-fields">
        <IntensityFields
          label="Ожидаемая интенсивность"
          count={expectedCount}
          period={expectedPeriod}
          onCount={setExpectedCount}
          onPeriod={setExpectedPeriod}
        />
        <IntensityFields
          label="Фактическая интенсивность"
          count={actualCount}
          period={actualPeriod}
          onCount={setActualCount}
          onPeriod={setActualPeriod}
        />
        <button
          type="button"
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              expected: intensityPayload(expectedCount, expectedPeriod),
              actual: intensityPayload(actualCount, actualPeriod),
            })
          }
        >
          Сохранить интенсивность
        </button>
      </div>

      <p className="muted small">
        Процессы не удаляются — отработавший переводится в архив. На процесс ссылаются, и
        исчезнувшая строка превратила бы эти ссылки в загадку.
      </p>

      {error && <p className="bad form-error">{error}</p>}
    </div>
  )
}

function ProcessCard({
  processId,
  isAdmin,
  onBack,
}: {
  processId: number
  isAdmin: boolean
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const process = useQuery({
    queryKey: ['process', processId],
    queryFn: () => fetchProcess(processId),
  })

  const onOpenStage = useCallback((stage: ProcessStage) => setSelectedKey(stage.key), [])

  const applyDetail = (detail: ProcessDetail) => {
    queryClient.setQueryData(['process', processId], detail)
    queryClient.invalidateQueries({ queryKey: ['processes'] })
    queryClient.invalidateQueries({ queryKey: ['audit'] })
  }

  const removeStage = useMutation({
    mutationFn: (stageId: number) => deleteStage(processId, stageId),
    onSuccess: applyDetail,
  })
  const reorder = useMutation({
    mutationFn: (stageIds: number[]) => reorderStages(processId, stageIds),
    onSuccess: applyDetail,
  })
  const removeTransition = useMutation({
    mutationFn: (edgeId: number) => deleteTransition(processId, edgeId),
    onSuccess: applyDetail,
  })
  const relabelTransition = useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) =>
      updateTransition(processId, id, label.trim() || null),
    onSuccess: applyDetail,
  })
  const dropParticipant = useMutation({
    mutationFn: (participantId: number) => removeParticipant(processId, participantId),
    onSuccess: applyDetail,
  })

  if (process.isPending) return <p className="muted">Загружаем процесс…</p>
  if (process.isError || !process.data) return <p className="bad">Не удалось загрузить процесс</p>

  const detail = process.data
  const titleOf = (key: string) => detail.nodes.find((node) => node.key === key)?.title ?? key

  /** Переставляет этап на соседнее место и отправляет весь порядок целиком. */
  const move = (index: number, delta: number) => {
    const ids = detail.nodes.map((node) => node.id)
    const target = index + delta
    if (target < 0 || target >= ids.length) return

    const [moved] = ids.splice(index, 1)
    if (moved === undefined) return
    ids.splice(target, 0, moved)
    reorder.mutate(ids)
  }

  return (
    <section>
      <div className="card">
        <div className="card-head">
          <button type="button" className="link" onClick={onBack}>
            ← К реестру
          </button>
          <span className={`badge status-${detail.status}`}>{STATUS_LABELS[detail.status]}</span>
        </div>

        <h2>{detail.name}</h2>
        {detail.description && <p>{detail.description}</p>}

        <dl className="facts">
          <div>
            <dt>Регулярность</dt>
            <dd>{SCHEDULE_LABELS[detail.schedule]}</dd>
          </div>
          <div>
            <dt>Ответственный</dt>
            <dd>{detail.owner_name ?? '—'}</dd>
          </div>
          <div>
            <dt>Ожидаемая интенсивность</dt>
            <dd>{formatIntensity(detail.expected_count, detail.expected_period)}</dd>
          </div>
          <div>
            <dt>Фактическая интенсивность</dt>
            <dd>{formatIntensity(detail.actual_count, detail.actual_period)}</dd>
          </div>
        </dl>

        {detail.problems.length > 0 && (
          <div className="problems">
            <strong>Процесс описан не до конца</strong>
            <ul>
              {detail.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Схема</h2>
        <ProcessGraph
          stages={detail.nodes}
          transitions={detail.edges}
          selectedKey={selectedKey}
          onOpen={onOpenStage}
        />
      </div>

      <div className="card">
        <h2>Этапы</h2>

        {detail.nodes.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Этап</th>
                <th>Тип</th>
                <th>Исполнитель</th>
                <th>Лейн нити</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {detail.nodes.map((stage, index) => (
                <tr key={stage.id} className={stage.key === selectedKey ? 'is-selected' : undefined}>
                  <td>{stage.title}</td>
                  <td>{NODE_TYPE_LABELS[stage.type]}</td>
                  <td>{stage.actor_name ?? '—'}</td>
                  <td>
                    {stage.lane_department ?? (
                      <span className="warn-inline" title="Этап без подразделения не попадёт в нить">
                        не выводится
                      </span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="actions">
                      <button
                        type="button"
                        title="Выше по списку"
                        disabled={index === 0 || reorder.isPending}
                        onClick={() => move(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        title="Ниже по списку"
                        disabled={index === detail.nodes.length - 1 || reorder.isPending}
                        onClick={() => move(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        disabled={removeStage.isPending}
                        onClick={() => removeStage.mutate(stage.id)}
                      >
                        Удалить
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {isAdmin && detail.nodes.length > 1 && (
          <p className="muted small">
            Порядок задаёт этот список и выбор в переходах. Схему он не меняет — её строит
            раскладка по переходам, — а номера на нити считаются обходом от «начала».
          </p>
        )}

        {isAdmin && <StageForm process={detail} />}
      </div>

      <div className="card">
        <h2>Переходы</h2>

        {detail.edges.length === 0 && <p className="muted">Переходов пока нет</p>}

        {detail.edges.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Из</th>
                <th>В</th>
                <th>Условие</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {detail.edges.map((transition) => (
                <tr key={transition.id}>
                  <td>{titleOf(transition.from_node_key)}</td>
                  <td>{titleOf(transition.to_node_key)}</td>
                  <td>
                    {isAdmin ? (
                      <input
                        defaultValue={transition.label ?? ''}
                        placeholder="без условия"
                        onBlur={(event) => {
                          if (event.target.value !== (transition.label ?? '')) {
                            relabelTransition.mutate({
                              id: transition.id,
                              label: event.target.value,
                            })
                          }
                        }}
                      />
                    ) : (
                      (transition.label ?? '—')
                    )}
                  </td>
                  {isAdmin && (
                    <td className="actions">
                      <button
                        type="button"
                        disabled={removeTransition.isPending}
                        onClick={() => removeTransition.mutate(transition.id)}
                      >
                        Удалить
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {isAdmin && detail.nodes.length >= 2 && <TransitionForm process={detail} />}
      </div>

      <div className="card">
        <h2>Участники</h2>

        {detail.participants.length === 0 && <p className="muted">Участников пока нет</p>}

        {detail.participants.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Участник</th>
                <th>Кто это</th>
                <th>Роль в процессе</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {detail.participants.map((participant) => (
                <tr key={participant.id}>
                  <td>{participant.name}</td>
                  <td className="muted">
                    {participant.employee_id !== null ? 'сотрудник' : 'подразделение'}
                  </td>
                  <td>{participant.note ?? '—'}</td>
                  {isAdmin && (
                    <td className="actions">
                      <button
                        type="button"
                        disabled={dropParticipant.isPending}
                        onClick={() => dropParticipant.mutate(participant.id)}
                      >
                        Убрать
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {isAdmin && <ParticipantForm process={detail} />}
      </div>

      {isAdmin && (
        <div className="card">
          <h2>Настройки процесса</h2>
          <ProcessSettings process={detail} />
        </div>
      )}
    </section>
  )
}

export default function ProcessesPage({ currentUser }: { currentUser: UserProfile }) {
  const isAdmin = currentUser.role === 'admin'
  const [openId, setOpenId] = useState<number | null>(null)

  if (openId !== null) {
    return (
      <ProcessCard processId={openId} isAdmin={isAdmin} onBack={() => setOpenId(null)} />
    )
  }
  return <ProcessRegistry isAdmin={isAdmin} onOpen={setOpenId} />
}
